import { useTranslate } from 'shared/lib/i18n'
import { CompactTable, EmptyTableBody, SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { actorWords, causeWords, holderWords, type HistoryEntry } from './lookup-words'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * A Parcel's history for people (ticket 48, user stories 114, 126): each row says
 * who (a Staff member by name and role, the partner by name, the system, the Shadow),
 * where (the place by name and ID), the holder before → after, the cause in words
 * with what it names (a Trip's code, a batch, a Bag's code), the comment, the
 * document's mask. The rows of the Bag or Box it rode in — sealed, handed to the
 * driver, taken by the Point, opened — are marked with that container, in the one
 * order of time. No raw id is shown, and no cause the screen has no words for.
 *
 * One table: when, what, who, where, and the details (the holder before → after, the
 * cause, the comment) — newest last, as the Event Log reads.
 */


function rowTitle(t: ReturnType<typeof useTranslate>['t'], entry: HistoryEntry): string {
	// Out of LOST, an arrival is the find (ticket 43).
	if (entry.from === 'LOST') return t('parcelEventKind.found')
	// Shelved straight from its row by an inventory's scan (ticket 54).
	if (entry.kind === 'shelved' && entry.cause.type === 'inventory') return t('parcelEventKind.inventory_taken')
	return t(`parcelEventKind.${entry.kind}` as never)
}

export function ParcelHistory({ history }: { history: HistoryEntry[] }) {
	const { t } = useTranslate()

	return (
		<section className='flex flex-col gap-2' data-testid='parcel-history'>
			<SectionTitle title={t('parcels.historyTitle')} count={history.length} />
			<CompactTable>
				<TableHeader>
					<TableRow>
						<TableHead className='w-32'>{t('parcels.historyWhen')}</TableHead>
						<TableHead>{t('parcels.historyWhat')}</TableHead>
						<TableHead>{t('parcels.historyWho')}</TableHead>
						<TableHead>{t('parcels.historyWhere')}</TableHead>
						<TableHead>{t('parcels.historyDetails')}</TableHead>
					</TableRow>
				</TableHeader>
				{history.length === 0 ? (
					<EmptyTableBody columns={5}>{t('parcels.historyEmpty')}</EmptyTableBody>
				) : (
					<TableBody data-testid='parcel-history-rows'>
						{history.map((entry, index) => (
							<TableRow
								key={index}
								data-testid='history-row'
								data-kind={entry.kind}
								className='align-top hover:bg-transparent'
							>
								<TableCell className='whitespace-nowrap text-muted-foreground tabular-nums'>
									{formatDayTime(entry.at)}
								</TableCell>
								<TableCell className='whitespace-normal'>
									<div className='flex flex-wrap items-center gap-1.5'>
										<span className='font-medium'>{rowTitle(t, entry)}</span>
										{entry.to && !entry.marker && !entry.pointChange && !entry.factChange && (
											// The stage it came to, when it changed: «→ На полке».
											<span className='text-muted-foreground'>→ {t(`parcelState.${entry.to}` as never)}</span>
										)}
										{entry.marker && (
											// A marker trace moves no state: it records WHICH marker changed (ADR-0005).
											<Badge variant='outline'>{t(`parcelMarker.${entry.marker}` as never)}</Badge>
										)}
										{entry.container && (
											<Badge variant='outline' className='font-mono' data-testid='history-container'>
												{holderWords(t, {
													type: 'container',
													holderId: entry.container.containerId,
													code: entry.container.code,
													kind: entry.container.kind,
												})}
											</Badge>
										)}
									</div>
								</TableCell>
								<TableCell className='whitespace-normal' data-testid='history-who'>
									{actorWords(t, entry)}
								</TableCell>
								<TableCell className='text-muted-foreground' data-testid='history-where'>
									{entry.location ? entry.location.id || '—' : ''}
								</TableCell>
								<TableCell className='whitespace-normal'>
									<div className='flex flex-col gap-0.5 text-muted-foreground'>
										{(entry.pointChange || entry.factChange) && <EditRow entry={entry} />}
										{(entry.fromHolder || entry.toHolder) && (
											<span data-testid='history-holders'>
												{t('historyRow.holders', {
													from: entry.fromHolder ? holderWords(t, entry.fromHolder) : '—',
													to: entry.toHolder ? holderWords(t, entry.toHolder) : '—',
												})}
											</span>
										)}
										{causeWords(t, entry) && <span data-testid='history-cause'>{causeWords(t, entry)}</span>}
										{entry.releasedWhere && (
											<span data-testid='history-released-where'>
												{t('parcels.historyReleasedWhere')}: {t(`parcelReleasedWhere.${entry.releasedWhere}`)}
											</span>
										)}
										{entry.note && <span className='text-foreground'>«{entry.note}»</span>}
										{entry.documentMask && (
											<span>
												{t('parcels.historyDocument')}: {entry.documentMask}
											</span>
										)}
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				)}
			</CompactTable>
		</section>
	)
}

/**
 * An edit row of the history (ticket 44): "Point changed: A → B", or what of the
 * Recipient and the amount changed, "was → now" — the document only by its mask —
 * and who it was by: the partner by its API, a re-sent batch, the partner's file,
 * support, the Shadow.
 */
function EditRow({ entry }: { entry: HistoryEntry }) {
	const { t } = useTranslate()
	const by = t(`parcelEdit.by_${entry.cause.type}` as never)
	const sum = (n: number) => n.toLocaleString('ru-RU')
	const lines: string[] = []
	if (entry.pointChange) {
		lines.push(
			t('parcelEdit.historyPoint', {
				from: entry.pointChange.from.id || '—',
				to: entry.pointChange.to.id || '—',
			}),
		)
	}
	const change = entry.factChange
	if (change?.recipientName) lines.push(t('parcelEdit.historyName', change.recipientName))
	if (change?.clientRef) lines.push(t('parcelEdit.historyClientRef', change.clientRef))
	if (change?.documentMask) {
		lines.push(t('parcelEdit.historyDocument', { from: change.documentMask.from ?? '—', to: change.documentMask.to }))
	}
	if (change?.amountOwed) {
		lines.push(t('parcelEdit.historyAmount', { from: sum(change.amountOwed.from), to: sum(change.amountOwed.to) }))
	}
	return (
		<div className='flex flex-col gap-0.5' data-testid='history-edit'>
			{lines.map((line) => (
				<span key={line}>
					<span className='text-foreground'>{line}</span> · {by}
				</span>
			))}
		</div>
	)
}
